import React, { useState, useEffect } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import Helmet from 'react-helmet'
import AppHeader from '../../../../components/AppView/Header'
import moment from 'moment'
import { AppMainView } from '../../../../components/AppView'
import { groupBy, values } from 'lodash'
import { IPaginatedResponse } from '@getgreenline/homi-shared'
import { InventoryLogAPI } from '@getgreenline/inventory'
import { IInventoryLog, InventoryLogType } from '@getgreenline/inventory/build/models/inventoryLog'
import { ILocationContract } from '@getgreenline/locations/build/models'
import { DatePicker } from 'antd'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import {
  GrRegex,
  getTotalUnitDiscrepancies,
  parseErrorMsg,
  sortLogsByTotalDiscrepancy,
} from '../../../../utilities/helpers'
import { BlazeCRSALocationSelector, ICRSALocations } from '../BlazeCRSALocationSelector'
import { InventoryCorrectionsLogs } from './InventoryCorrectionsLogs'
import { BlazeAlert } from '../../../../components/BlazeAlert/BlazeAlert'
import { THEME_COLORS } from '@getgreenline/shared'
import './InventoryCorrections.scss'
import { getMonthStartEnd } from '../../../../utilities/date'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

const _InventoryCorrections = ({ currentCompanyStore }: InjectedProps) => {
  const defaultDate = new Date()
  const defaultMonthStart = getMonthStartEnd(defaultDate).monthStart
  const defaultMonthEnd = getMonthStartEnd(defaultDate).monthEnd

  const [locationsWithCRSA, setLocationsWithCRSA] = useState<ILocationContract[]>([])
  const [selectedCRSA, setSelectedCRSA] = useState<ICRSALocations | undefined>()
  const [logs, setLogs] = useState<IInventoryLog[][]>([])
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(defaultMonthStart)
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(defaultMonthEnd)
  const [isLoading, setIsLoading] = useState(false)
  const [showInstructionsMessage, setShowInstructionMessage] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const company = currentCompanyStore?.company
  const { MonthPicker } = DatePicker

  const flattenedLogs = logs.flat()
  const { total, sumOfAdditions, sumOfReductions } = getTotalUnitDiscrepancies(flattenedLogs)
  const showLogs = logs.length > 0
  const showSuccessMessage = logs.length === 0 && !showInstructionsMessage

  const getLocationsWithCRSA = (locations: ILocationContract[] | undefined) => {
    const isValidCRSALocationFilter = (location: ILocationContract) =>
      GrRegex.isValidCRSA(location.storeId || '')

    const locationsWithCRSA: ILocationContract[] = locations
      ? locations.filter(isValidCRSALocationFilter)
      : []

    setLocationsWithCRSA(locationsWithCRSA)
    return locationsWithCRSA
  }

  const processLogs = (logs: IPaginatedResponse<IInventoryLog>[]) => {
    const logsFiltered = logs
      .flatMap((log) => log.data)
      .filter((log) => log.updateValue !== 0 && !!log.complianceCategoryId)

    const logsGroupedByProductId = values(groupBy(logsFiltered, 'productId'))

    return logsGroupedByProductId
  }

  const loadLogs = async () => {
    setIsLoading(true)
    setErrorMessage(undefined)

    const selectedCRSALocations = selectedCRSA?.locations

    try {
      if (!company) {
        throw new Error('No identified company')
      }
      if (!selectedCRSA) {
        throw new Error('No CRSA selected')
      }
      if (!selectedCRSALocations) {
        throw new Error('No location associated with this CRSA')
      }
      if (!selectedStartDate || !selectedEndDate) {
        throw new Error('No month selected')
      }
      const monthDifference =
        selectedEndDate.getMonth() -
        selectedStartDate.getMonth() +
        12 * (selectedEndDate.getFullYear() - selectedStartDate.getFullYear())
      if (monthDifference > 1) {
        throw new Error('Selected date range should not exceed one month')
      }
      const logsPromises = selectedCRSALocations.map((location) => {
        return InventoryLogAPI.getInventoryLogs(company.id, {
          locationId: location.id,
          type: InventoryLogType.AUDIT,
          startDate: selectedStartDate,
          endDate: selectedEndDate,
        })
      })
      const logsFromLocations = await Promise.all(logsPromises)
      setLogs(processLogs(logsFromLocations))
      setShowInstructionMessage(false)
    } catch (error) {
      console.error(error)
      setErrorMessage(parseErrorMsg(error) || 'Failed to fetch inventory logs')
    } finally {
      setIsLoading(false)
    }
  }

  const getLocationIds = (locations: ILocationContract[]) => {
    const locationIds = locations.map((location) => location.id)
    return locationIds
  }

  const setDates = (date: moment.Moment | null) => {
    if (!date) return
    const vanillaDate = date.toDate()
    const { monthStart, monthEnd } = getMonthStartEnd(vanillaDate)
    setSelectedStartDate(monthStart)
    setSelectedEndDate(monthEnd)
  }

  const resetFilters = () => {
    setSelectedCRSA(undefined)
    setSelectedStartDate(defaultMonthStart)
    setSelectedEndDate(defaultMonthEnd)
    setLogs([])
    setShowInstructionMessage(true)
  }

  useEffect(() => {
    getLocationsWithCRSA(currentCompanyStore?.locations)
  }, [])

  return (
    <>
      <div className='d-flex flex-column h-100'>
        <Helmet>
          <title>Compliance | Inventory Corrections</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Inventory Corrections' />

        <AppMainView className='background-grey intercom-pb-2'>
          <div className='white-rounded-container'>
            <div className='text-muted mb-2'>View and correct your inventory audits below.</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <MonthPicker
                className='inventory-corrections-month-picker mr-2 mt-1'
                value={moment(selectedStartDate)}
                onChange={setDates}
                allowClear={false}
              />
              <div className='mt-1'>
                <BlazeCRSALocationSelector
                  locations={locationsWithCRSA}
                  value={selectedCRSA?.crsa}
                  onSelect={({ crsaLocation }) => setSelectedCRSA(crsaLocation)}
                />
              </div>
              <BlazeButton
                buttonProps={{
                  className: 'mt-1',
                  loading: isLoading,
                  disabled: isLoading || !selectedCRSA,
                  type: 'primary',
                  onClick: () => {
                    loadLogs()
                  },
                }}
                titleProps={{
                  title: 'Apply',
                }}
              />
              <BlazeButton
                buttonProps={{
                  className: 'mt-1',
                  type: 'link',
                  disabled: !selectedCRSA,
                  onClick: resetFilters,
                }}
                titleProps={{ title: 'Reset' }}
              />
            </div>
          </div>

          {showLogs && selectedStartDate && selectedEndDate && selectedCRSA && (
            <>
              <BlazeAlert type='warning' style={{ marginTop: -8, marginBottom: 8 }} showIcon>
                These products will show up as "other additions/reductions" in your compliance.
                <br />
                Select "modify" in each row and choose either a specific reason or choose "audit"
                and its reason.
              </BlazeAlert>

              <div className='table table-responsive'>
                <table className='table table-hover border'>
                  <thead
                    className='thead'
                    style={{ backgroundColor: THEME_COLORS.MONO.monoF5f7fa }}
                  >
                    <tr>
                      <th>SKU</th>
                      <th>Product</th>
                      <th className='text-right'>Additions</th>
                      <th className='text-right'>Reductions</th>
                      <th className='text-right'>Total</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {logs.sort(sortLogsByTotalDiscrepancy).map((logs) => (
                      <InventoryCorrectionsLogs
                        key={logs[0].productId}
                        logs={logs}
                        startDate={selectedStartDate}
                        endDate={selectedEndDate}
                        locationIds={getLocationIds(selectedCRSA.locations)}
                        onUpdate={() => loadLogs()}
                      />
                    ))}
                    <tr style={{ backgroundColor: THEME_COLORS.MONO.monoF5f7fa }}>
                      <td colSpan={2}>
                        <b>Total unit discrepancies</b>
                      </td>
                      <td className='text-right text-success'>+{Math.round(sumOfAdditions)}</td>
                      <td className='text-right text-danger'>{Math.round(sumOfReductions)}</td>
                      <td className={`text-right ${total > 0 ? 'text-success' : 'text-danger'}`}>
                        <b>{total > 0 ? `+${Math.round(total)}` : Math.round(total)}</b>
                      </td>
                      <td colSpan={1}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          {showSuccessMessage && (
            <BlazeAlert type='success' style={{ marginTop: -8 }} showIcon>
              Great job! All of your inventory logs are valid and do not require anymore attention.
            </BlazeAlert>
          )}

          {showInstructionsMessage && (
            <BlazeAlert type='info' style={{ marginTop: -8 }} showIcon>
              Please select a date range and CRSA number to display audited inventory logs.
            </BlazeAlert>
          )}

          {errorMessage && (
            <BlazeAlert type='error' showIcon style={{ marginTop: 5 }}>
              {errorMessage}
            </BlazeAlert>
          )}
        </AppMainView>
      </div>
    </>
  )
}

export const InventoryCorrections = inject('currentCompanyStore')(observer(_InventoryCorrections))
